import { Button, Dialog, Icon, Position, Size, Tooltip } from '@busybusy/webapp-react-ui';
import { CloseIcon } from 'assets/icons';
import classNames from 'classnames';
import ExclusiveOfferView from 'containers/settings/subscriptions/ExclusiveOfferView/ExclusiveOfferView';
import useSubscriptionGraylog from 'hooks/analytics/useSubscriptionGraylog';
import useNavigation from 'hooks/navigation/useNavigation';
import useActiveMember from 'hooks/store/useActiveMember';
import { t } from 'i18next';
import { ReactNode } from 'react';
import { ClassName } from 'types/ClassName';
import { useFeatureFlags } from 'utils/features';
import { isAdmin } from 'utils/positionUtils';
import './UpgradePromptView.scss';

export type FreemiumViewType =
  | 'account_changed'
  | 'gps_unavailable'
  | 'projects_limited'
  | 'cost_codes_limited'
  | 'equipment_limited';

export interface IUpgradePromptViewProps {
  className?: ClassName;
  icon?: ReactNode;
  title: ReactNode;
  description: ReactNode;
  children?: ReactNode;
  offerDescription: ReactNode;
  footer?: ReactNode;
  enableCloseGuard?: boolean;
  viewType: FreemiumViewType;
  isOpen: boolean;
  onClose: () => void;
}

const UpgradePromptView = (props: IUpgradePromptViewProps) => {
  const {
    className,
    icon,
    title,
    description,
    children,
    offerDescription,
    footer,
    enableCloseGuard,
    viewType,
    isOpen,
    onClose,
  } = props;

  const activeMember = useActiveMember();
  const adminViewer = isAdmin(activeMember.position);
  const { reviewPlans, upgradePlan } = useSubscriptionGraylog();
  const navigateToSubscription = useNavigation('/settings/subscription');
  // FREEMIUM_LIMITED is LaunchDarkly-targeted to the orgs eligible for the reduced-price offer; those
  // orgs see the "Review Plans" offer, everyone else just gets a plain "Upgrade".
  const isFreemiumLimited = useFeatureFlags('FREEMIUM_LIMITED');

  const guardClose = !!enableCloseGuard && adminViewer;
  const guardActionLabel = isFreemiumLimited ? t('Review Plans') : t('Upgrade');
  const guardMessage = t('Select "{{action}}" to further understand next steps and to close this page.', {
    action: guardActionLabel,
  });
  const classes = classNames('upgrade-prompt-view', className);

  function handleUpgrade() {
    upgradePlan(viewType);
    navigateToSubscription();
    onClose();
  }

  function handleReviewPlans() {
    reviewPlans(viewType);
    onClose();
  }

  function renderCta() {
    if (!adminViewer) {
      return (
        <Button className="px-6 m-6" type="secondary" onClick={onClose}>
          {t('Close')}
        </Button>
      );
    }

    if (!isFreemiumLimited) {
      return (
        <Button className="upgrade-button mx-6" type="primary" onClick={handleUpgrade}>
          {t('Upgrade')}
        </Button>
      );
    }

    return <ExclusiveOfferView description={offerDescription} onReviewPlans={handleReviewPlans} />;
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className={classes}
      hasDismiss={!guardClose}
      allowOverlayClose={!guardClose}
      buttons={
        guardClose ? (
          <Tooltip label={guardMessage} delay={0} position={Position.BOTTOM}>
            <Button size={Size.SMALL} type="icon" onClick={() => undefined}>
              <Icon svg={CloseIcon} />
            </Button>
          </Tooltip>
        ) : undefined
      }
    >
      <div className="p-6">
        {icon && <div className="prompt-icon">{icon}</div>}
        <h2 className="prompt-title">{title}</h2>
        <div className="prompt-description">{description}</div>
        {children}
        {renderCta()}
        {footer && <div className="prompt-description pb-6">{footer}</div>}
      </div>
    </Dialog>
  );
};

export default UpgradePromptView;
